import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  IconButton, 
  Tooltip, 
  Menu, 
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import SettingsIcon from '@mui/icons-material/Settings';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import PaletteIcon from '@mui/icons-material/Palette';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { logout } from '../store/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import UnitDropdown from './UnitDropdown/UnitDropdown';
import { useThemeContext } from '../context/ThemeContext';
import BookmarkTutorial from './BookmarkTutorial/BookmarkTutorial';
import NotificationBell from './Notifications/NotificationBell';
import ThemeSettings from './Settings/ThemeSettings';
import { hasActivityAccess } from '../utils/activityAccess';

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const signedEula = useSelector(state => state.auth.signedEula);
  const activeUnit = useSelector(state => state.auth.activeUnit);
  const activityEnabled = hasActivityAccess(activeUnit);
  const { toggleColorMode, mode } = useThemeContext();
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [themeSettingsOpen, setThemeSettingsOpen] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
  
  const settingsMenuOpen = Boolean(settingsMenuAnchor);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleSettingsMenuOpen = (event) => {
    setSettingsMenuAnchor(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsMenuAnchor(null);
  };

  const handleThemeToggle = () => {
    toggleColorMode();
    handleSettingsMenuClose();
  };

  const handleThemeSettings = () => {
    setThemeSettingsOpen(true);
    handleSettingsMenuClose();
  };

  const handleTutorialOpen = () => {
    setTutorialOpen(true);
    handleSettingsMenuClose();
  };

  const handleProfileNavigate = () => {
    navigate('/profile');
    handleSettingsMenuClose();
  };

  const handleLogoutFromMenu = () => {
    handleSettingsMenuClose();
    handleLogout();
  };  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Box 
            component={Link} 
            to="/dashboard" 
            sx={{ 
              flexGrow: 1, 
              textDecoration: 'none', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <img 
              src="/icons/Connect App Logo v2 white.svg"
              alt="Connect App 365" 
              style={{ height: '40px' }}
            />
          </Box>

          {isAuthenticated && signedEula && activityEnabled && (
            <Tooltip title="Activity">
              <IconButton
                color="inherit"
                onClick={() => navigate('/activity')}
                aria-label="Open activity insights"
                sx={{ mr: 0.5 }}
              >
                <ShowChartIcon />
              </IconButton>
            </Tooltip>
          )}
          
          {isAuthenticated && (
            <NotificationBell />
          )}
          
          {/* Settings Menu */}
          <Tooltip title="Settings">
            <IconButton 
              color="inherit" 
              onClick={handleSettingsMenuOpen}
              sx={{ mr: 2 }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={settingsMenuAnchor}
            open={settingsMenuOpen}
            onClose={handleSettingsMenuClose}
            MenuListProps={{
              'aria-labelledby': 'settings-button',
            }}
          >
            <MenuItem onClick={handleProfileNavigate}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleThemeToggle}>
              <ListItemIcon>
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </ListItemIcon>
              <ListItemText primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'} />
            </MenuItem>
            <MenuItem onClick={handleThemeSettings}>
              <ListItemIcon>
                <PaletteIcon />
              </ListItemIcon>
              <ListItemText primary="Theme Settings" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleTutorialOpen}>
              <ListItemIcon>
                <BookmarkBorderIcon />
              </ListItemIcon>
              <ListItemText primary="Bookmark Tutorial" />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogoutFromMenu}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      {isAuthenticated && signedEula && (
        <Box sx={{ 
          padding: '1rem',
        }}>
          <UnitDropdown />
        </Box>
      )}
      
      <BookmarkTutorial 
        open={tutorialOpen} 
        onClose={() => setTutorialOpen(false)} 
      />
      
      <ThemeSettings
        open={themeSettingsOpen}
        onClose={() => setThemeSettingsOpen(false)}
      />
    </>
  );
};

export default Header;
